/*eslint max-lines-per-function: ["error", 200]*/
import type { TFunction } from 'i18next';

import { DefaultApi, GetClientSurveyDto } from '../autogenerated/pokApiClient';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { TKeys } from '../../hooks/useTranslation';
import { clientSurveyForGrid } from '../../../pok/components/ClientSurveys/ClientSurveySearch';

export interface ClientSurveyContextInterface {
  getAllWaitingToSendByPage: (
    t: TFunction<'administrationLng', 'administrationLng'>,
    tk: TKeys<'administrationLng'>,
    lang: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllWaitingToSendCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;

  getAllWaitingForAnswerByPage: (
    t: TFunction<'administrationLng', 'administrationLng'>,
    tk: TKeys<'administrationLng'>,
    lang: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllWaitingForAnswerCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;

  getAllByPage: (
    t: TFunction<'administrationLng', 'administrationLng'>,
    tk: TKeys<'administrationLng'>,
    lang: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;

  getAllWithAnswerAndErrorsByPage: (
    t: TFunction<'administrationLng', 'administrationLng'>,
    tk: TKeys<'administrationLng'>,
    lang: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllWithAnswerAndErrorsCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;

  getAllWithAnsweredByPage: (
    t: TFunction<'administrationLng', 'administrationLng'>,
    tk: TKeys<'administrationLng'>,
    lang: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllWithAnsweredCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;

  getAllExpiredWithoutReminderByPage: (
    t: TFunction<'administrationLng', 'administrationLng'>,
    tk: TKeys<'administrationLng'>,
    lang: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    setRefresh?: (refresh: boolean) => void,
  ) => Promise<GridRecord[]>;
  getAllExpiredWithoutReminderCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  repeatClientSurvey: (id: string) => Promise<GetClientSurveyDto>;
}

export const ClientSurveyContext = (api: DefaultApi) => {
  return {
    getAllWaitingToSendByPage: async (
      t: TFunction<'administrationLng', 'administrationLng'>,
      tk: TKeys<'administrationLng'>,
      lang: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.clientSurveyControllerGetAllWaitingToSendByPage(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => clientSurveyForGrid(t, tk, item, lang));
    },

    getAllWaitingToSendCount: (filterText?: string, filterColumns?: string) =>
      api.clientSurveyControllerGetAllWaitingToSendCount(
        filterText || '',
        filterColumns,
      ),

    getAllWaitingForAnswerByPage: async (
      t: TFunction<'administrationLng', 'administrationLng'>,
      tk: TKeys<'administrationLng'>,
      lang: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.clientSurveyControllerGetAllWaitingForAnswerByPage(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => clientSurveyForGrid(t, tk, item, lang));
    },

    getAllWaitingForAnswerCount: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.clientSurveyControllerGetAllWaitingForAnswerCount(
        filterText || '',
        filterColumns,
      ),

    getAllByPage: async (
      t: TFunction<'administrationLng', 'administrationLng'>,
      tk: TKeys<'administrationLng'>,
      lang: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.clientSurveyControllerGetAllByPage(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => clientSurveyForGrid(t, tk, item, lang));
    },

    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.clientSurveyControllerGetAllCount(filterText || '', filterColumns),

    getAllWithAnswerAndErrorsByPage: async (
      t: TFunction<'administrationLng', 'administrationLng'>,
      tk: TKeys<'administrationLng'>,
      lang: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.clientSurveyControllerGetAllWithAnswerAndErrorsByPage(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(item => clientSurveyForGrid(t, tk, item, lang));
    },

    getAllWithAnswerAndErrorsCount: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.clientSurveyControllerGetAllWithAnswerAndErrorsCount(
        filterText || '',
        filterColumns,
      ),

    getAllWithAnsweredByPage: async (
      t: TFunction<'administrationLng', 'administrationLng'>,
      tk: TKeys<'administrationLng'>,
      lang: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.clientSurveyControllerGetAllWithAnsweredByPage(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => clientSurveyForGrid(t, tk, item, lang));
    },

    getAllWithAnsweredCount: (filterText?: string, filterColumns?: string) =>
      api.clientSurveyControllerGetAllWithAnsweredCount(
        filterText || '',
        filterColumns,
      ),

    getAllExpiredWithoutReminderByPage: async (
      t: TFunction<'administrationLng', 'administrationLng'>,
      tk: TKeys<'administrationLng'>,
      lang: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      setRefresh?: (refresh: boolean) => void,
    ) => {
      const data =
        await api.clientSurveyControllerGetAllExpiredWithoutReminderByPage(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(item =>
        clientSurveyForGrid(t, tk, item, lang, setRefresh),
      );
    },

    getAllExpiredWithoutReminderCount: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.clientSurveyControllerGetAllExpiredWithoutReminderCount(
        filterText || '',
        filterColumns,
      ),

    repeatClientSurvey: (id: string) => api.clientSurveyControllerRepeat(id),
  };
};
